import { useCallback, useEffect, useRef, useState } from "react";
import { loadedRelease, parseRelease, type Release } from "./release";

export function ReleaseStatus({ canReload, onReload }: { canReload: boolean; onReload: () => void }) {
  const [remote, setRemote] = useState<Release | null>(null);
  const [state, setState] = useState<"checking" | "checked" | "offline" | "unavailable">("checking");
  const pending = useRef<AbortController | null>(null);
  const check = useCallback(async () => {
    pending.current?.abort();
    pending.current = null;
    if (!navigator.onLine) { setRemote(null); setState("offline"); return; }
    const controller = new AbortController();
    pending.current = controller;
    const timeout = window.setTimeout(() => controller.abort(), 10000);
    setState("checking");
    setRemote(null);
    try {
      const response = await fetch("/version.json", { cache: "no-store", signal: controller.signal });
      if (!response.ok) throw new Error("Versionsprüfung nicht verfügbar");
      const release = parseRelease(await response.json());
      if (pending.current !== controller) return;
      setRemote(release);
      setState("checked");
    } catch {
      if (pending.current === controller) setState(navigator.onLine ? "unavailable" : "offline");
    } finally {
      window.clearTimeout(timeout);
    }
  }, []);
  useEffect(() => {
    void check();
    const visible = () => { if (document.visibilityState === "visible") void check(); };
    const connected = () => void check();
    document.addEventListener("visibilitychange", visible);
    window.addEventListener("online", connected);
    window.addEventListener("offline", connected);
    const interval = window.setInterval(visible, 60000);
    return () => {
      pending.current?.abort();
      pending.current = null;
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", visible);
      window.removeEventListener("online", connected);
      window.removeEventListener("offline", connected);
    };
  }, [check]);
  const different = remote && (remote.version !== loadedRelease.version || remote.version_stamp !== loadedRelease.version_stamp);
  const status = state === "checking" ? "Wird geprüft …" : state === "offline" ? "Offline · nicht geprüft"
    : state === "unavailable" ? "Online-Stand nicht prüfbar" : different
      ? remote.version_stamp > loadedRelease.version_stamp ? "Neue Version verfügbar" : "Abweichender Online-Stand"
      : "Aktuell";
  return <section className="release-status" aria-label="MemoHub-Version">
    <h2>Version</h2>
    <dl>
      <div><dt>Auf diesem Gerät geladen</dt><dd>{loadedRelease.version} · <code>{loadedRelease.version_stamp}</code></dd></div>
      <div><dt>Online verfügbar</dt><dd>{remote ? <>{remote.version} · <code>{remote.version_stamp}</code></> : "Nicht geprüft"}</dd></div>
    </dl>
    <p className="release-state" role="status">{status}</p>
    <small>Zeitstempel: YYYYMMDDHHMMSS · Europe/Berlin</small>
    <div className="release-actions">
      <button type="button" disabled={state === "checking"} onClick={() => void check()}>Version prüfen</button>
      {different && <button type="button" disabled={!canReload} onClick={onReload}>Jetzt aktualisieren</button>}
    </div>
    {different && !canReload && <small>Zum Aktualisieren zuerst offene Eingaben speichern oder schließen.</small>}
  </section>;
}
